import { Dialog, DialogActions, DialogContent } from "@mui/material";
import { Button, TextField } from "@platform/design-system";
import { useState } from "react";
import { z } from "zod";

interface AddContactDialogProps {
  open: boolean;
  label: string;
  onClose: () => void;
  onAddContact: (email: string) => string | void;
}

const EMAIL_MAX_LENGTH = 255;

const emailSchema = z
  .string()
  .min(5, { message: "Must be 5 or more characters long" })
  .max(EMAIL_MAX_LENGTH, {
    message: `Must be ${EMAIL_MAX_LENGTH} characters or less`,
  })
  .email("Invalid email address");

function AddContactDialog({
  open,
  onClose,
  onAddContact,
  label,
}: AddContactDialogProps): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const result = emailSchema.safeParse(email.trim());

    if (!result.success) {
      setError(result.error.errors[0].message);
      return;
    }

    const addContactError = onAddContact(result.data);
    if (addContactError) {
      setError(addContactError);
      return;
    }

    handleClose();
  };

  const handleClose = (): void => {
    setEmail("");
    setError(null);
    onClose();
  };

  const handleEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setEmail(event.target.value);
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          component: "form",
          onSubmit: handleSubmit,
        },
      }}
    >
      <DialogContent>
        <TextField
          margin="dense"
          label={`${label} Email`}
          fullWidth
          value={email}
          onChange={handleEmailChange}
          error={!!error}
          helperText={error}
          placeholder="example@company.com"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="tertiary">
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={!!error || !email.trim()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { AddContactDialog };
